import { Outlet, useMatches } from '@tanstack/react-router'
import { z } from 'zod'

export const PaginatedSearchSchema = z.object({
	limit: z.coerce.number().default(30),
	offset: z.coerce.number().default(0),
	q: z.string().optional(),
	sortBy: z.string().optional(),
	sortOrder: z.enum(['asc', 'desc']).optional()
})

// Builds a paginated search schema whose `sortBy` is constrained to the exact
// set of fields the backing endpoint can sort by. This keeps the URL contract
// in sync with the generated API client's `sortBy` union, so route search
// values flow into the query hooks without `as any` casts.
export function paginatedSearchWithSort<const T extends readonly [string, ...string[]]>(sortFields: T) {
	return PaginatedSearchSchema.extend({
		sortBy: z.enum(sortFields).optional()
	})
}

export function ConditionalRouteComponent({
	children,
	pathsToRenderOn,
	routePath
}: {
	children: React.ReactNode
	pathsToRenderOn: string[]
	routePath: string
}) {
	const matches = useMatches()
	const groupMatches = matches
		.filter(m => m.routeId?.startsWith(routePath))
		.map(m => m.routeId.replace(routePath, ''))
	const deepestId = groupMatches[groupMatches.length - 1]
	const normalizedDeepestId = deepestId && !deepestId.startsWith('/') ? `/${deepestId}` : deepestId
	const shouldShowChildren =
		!deepestId ||
		deepestId === '' ||
		deepestId === '/' ||
		pathsToRenderOn.some(p => normalizedDeepestId === p || normalizedDeepestId?.startsWith(`${p}/`))

	return shouldShowChildren ? children : <Outlet />
}
